import { resolveStorageDisplayUrl } from "./uploadResponseUtils";

/** Object key from a private key or an expired S3 signed URL. */
export function storageObjectKey(value?: string | null): string | undefined {
  const raw = String(value || "").trim();
  if (!raw) return undefined;
  if (!/^https?:\/\//i.test(raw)) return raw.split("?")[0] || undefined;
  try {
    const parsed = new URL(raw);
    if (!/amazonaws\.com|cloudfront\.net/i.test(parsed.hostname)) return undefined;
    let path = decodeURIComponent(parsed.pathname.replace(/^\//, ""));
    if (/^s3[.-]/i.test(parsed.hostname)) {
      path = path.replace(/^[^/]+\//, "");
    }
    return path || undefined;
  } catch {
    return undefined;
  }
}

/**
 * Browser-loadable hospital/doctor image. S3 signed URLs are converted to the
 * download proxy so expired signatures do not collapse to initials.
 */
export function resolveHospitalImageUrl(
  ...candidates: Array<string | null | undefined>
): string | undefined {
  for (const candidate of candidates) {
    const value = String(candidate || "").trim();
    if (!value) continue;
    if (value.startsWith("data:") || value.startsWith("blob:")) return value;
    const key = storageObjectKey(value);
    if (key) {
      const proxied = resolveStorageDisplayUrl(key);
      if (proxied) return proxied;
    }
    if (/^https?:\/\//i.test(value)) return value;
    const proxied = resolveStorageDisplayUrl(value);
    if (proxied) return proxied;
  }
  return undefined;
}

export type HospitalImageSource = {
  hospitalImage?: string | null;
  hospitalImageKey?: string | null;
  image?: string | null;
  logoUrl?: string | null;
  profileImage?: string | null;
  profileImageKey?: string | null;
};

export function hospitalImageFromSources(
  ...sources: Array<HospitalImageSource | string | null | undefined>
): string | undefined {
  const candidates: Array<string | null | undefined> = [];
  sources.forEach((source) => {
    if (!source) return;
    if (typeof source === "string") {
      candidates.push(source);
      return;
    }
    candidates.push(
      source.hospitalImage,
      source.hospitalImageKey,
      source.profileImage,
      source.profileImageKey,
      source.image,
      source.logoUrl,
    );
  });
  return resolveHospitalImageUrl(...candidates);
}
